import { Components, Theme } from '@mui/material';

/** The semantic colours that get palette-driven styling instead of the brand black. */
const SEMANTIC_COLORS = ['error', 'warning', 'success', 'info'] as const;
type SemanticColor = (typeof SEMANTIC_COLORS)[number];

const isSemantic = (color: unknown): color is SemanticColor => SEMANTIC_COLORS.includes(color as SemanticColor);

const MuiButtonOverrides: Components<Theme> = {
  MuiButton: {
    defaultProps: {
      variant: 'contained',
      disableElevation: true,
    },
    styleOverrides: {
      root: ({ ownerState: { variant, size, color }, theme }) => {
        const semantic = isSemantic(color) ? theme.palette[color] : undefined;

        return {
          flexShrink: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          borderRadius: '40px',
          textAlign: 'center',
          fontWeight: '500',
          border: 'none',
          transition: 'color 0.3s',
          textTransform: 'none',
          // inline-flex (not inline-block) so `startIcon`/`endIcon` - which inherit
          // this display - centre against the label instead of sitting on its baseline.
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
          height: '70px',
          fontSize: '20px',
          padding: '0 1.5em',
          backgroundColor: '#000',
          color: 'white',
          '&:hover': {
            backgroundColor: '#000',
            color: 'rgba(255, 255, 255, 0.5)',
          },

          ...(size === 'small' && {
            height: '36px',
            fontSize: '14px',
            padding: '0 1em',
          }),

          ...(variant === 'contained' && {
            backgroundColor: 'black',
          }),
          ...(variant === 'outlined' && {
            border: '1px solid rgba(0,0,0,0.5)',
            backgroundColor: 'transparent',
            color: 'black',
            transition: 'background 0.3s border-color 0.3s',
            '&:hover': {
              borderColor: 'black',
              backgroundColor: 'rgba(0,0,0,0.04)',
            },
          }),

          // A semantic `color` has to win over the brand black, otherwise a
          // destructive action is indistinguishable from a neutral one.
          ...(semantic &&
            variant === 'contained' && {
              backgroundColor: semantic.main,
              color: semantic.contrastText,
              '&:hover': {
                backgroundColor: semantic.dark ?? semantic.main,
                color: semantic.contrastText,
              },
            }),
          ...(semantic &&
            variant !== 'contained' && {
              backgroundColor: 'transparent',
              color: semantic.main,
              ...(variant === 'outlined' && { border: `1px solid ${semantic.main}` }),
              '&:hover': {
                color: semantic.dark ?? semantic.main,
                borderColor: semantic.dark ?? semantic.main,
                backgroundColor: theme.palette.action.hover,
              },
            }),
        };
      },
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        flexShrink: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        borderRadius: '40px',
        textAlign: 'center',
        fontWeight: '500',
        border: 'none',
        color: 'black',
        transition: 'color 0.3s',
        lineHeight: '70px',
        height: '70px',
        fontSize: '20px',
        padding: '1.2rem',
        textTransform: 'none',
        display: 'flex',
      },
    },
    variants: [
      {
        props: { color: 'dark' },
        style: {
          color: 'white',
          backgroundColor: 'black',
          '&:hover': {
            backgroundColor: 'black',
          },
        },
      },
      {
        props: { color: 'light' },
        style: {
          color: '#aaa',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
    ],
  },
};

export default MuiButtonOverrides;
